/**
 * Word-by-word reveal of the guide's latest reply.
 *
 * Driven from the render loop via `advance(delta)` rather than a `setInterval`.
 * A wall-clock timer is not frame-synced, so against a 72–90 Hz XR loop it
 * produces visibly uneven word appearance — and it keeps running while the
 * session is hidden, so the guide silently "speaks" into an idle headset.
 *
 * The mouth is driven off this service's state, so its pace IS the speaking
 * pace. If real synthesised audio is added later, re-anchor the mouth to the
 * audio clip instead or the two will drift apart.
 */
import { Store } from '../core/store.js';
import { CONFIG } from '../config.js';

export type PlaybackPhase = 'idle' | 'revealing' | 'finished';

export interface PlaybackState {
  /** Id of the message being revealed, or null. */
  messageId: string | null;
  /** The portion currently visible. */
  visibleText: string;
  phase: PlaybackPhase;
}

export class PlaybackService {
  readonly store = new Store<PlaybackState>({
    messageId: null,
    visibleText: '',
    phase: 'idle',
  });

  /** Source words, and where the whitespace between them was. */
  private tokens: string[] = [];
  private visibleCount = 0;
  private accumulator = 0;

  getSnapshot(): PlaybackState {
    return this.store.getSnapshot();
  }

  subscribe(listener: (state: PlaybackState) => void): () => void {
    return this.store.subscribe(listener);
  }

  /** True while words are still appearing — what the mouth animation follows. */
  get isSpeaking(): boolean {
    return this.store.getSnapshot().phase === 'revealing';
  }

  /**
   * Begin revealing a message. Calling this while another reveal is in flight
   * snaps the previous one to complete first, so no text is ever lost.
   */
  begin(messageId: string, text: string): void {
    // Split on whitespace but KEEP it, so the revealed text reproduces the
    // original spacing and line breaks exactly. Rejoining with single spaces
    // (as the reference implementation does) collapses newlines and makes the
    // bubble visibly reflow when the reveal finishes.
    this.tokens = text.match(/\s*\S+/g) ?? [];
    this.visibleCount = 0;
    this.accumulator = 0;

    if (this.tokens.length === 0) {
      this.store.set({ messageId, visibleText: '', phase: 'finished' });
      return;
    }
    this.store.set({ messageId, visibleText: '', phase: 'revealing' });
  }

  /**
   * Grow the text being revealed without restarting it.
   *
   * Tokens arrive while the reveal is already running, so the source text keeps
   * getting longer. Re-tokenising and keeping `visibleCount` means the reveal
   * simply has further to go — calling `begin` again would snap it back to the
   * start on every token.
   *
   * If the reveal had already caught up with the text it goes back to
   * revealing; the guide is still talking, it had just outrun the network.
   */
  extend(text: string): void {
    const phase = this.store.getSnapshot().phase;
    if (phase === 'idle') return;

    const tokens = text.match(/\s*\S+/g) ?? [];
    if (tokens.length <= this.tokens.length) return;

    this.tokens = tokens;
    this.publish(this.visibleCount >= this.tokens.length ? 'finished' : 'revealing');
  }

  /** Reveal everything immediately — used when the user asks to skip. */
  complete(): void {
    if (this.store.getSnapshot().phase !== 'revealing') return;
    this.visibleCount = this.tokens.length;
    this.publish('finished');
  }

  /** Clear back to idle, e.g. when the transcript is reset. */
  reset(): void {
    this.tokens = [];
    this.visibleCount = 0;
    this.accumulator = 0;
    this.store.set({ messageId: null, visibleText: '', phase: 'idle' });
  }

  /**
   * Advance the reveal. Call once per frame with the frame delta in seconds.
   *
   * Accumulates fractional words so the pace is correct regardless of frame
   * rate, and reveals however many words the elapsed time earned rather than
   * one per frame — a long frame must not silently slow the guide down.
   */
  advance(deltaSeconds: number): void {
    if (this.store.getSnapshot().phase !== 'revealing') return;

    this.accumulator += deltaSeconds * CONFIG.chat.revealWordsPerSecond;
    if (this.accumulator < 1) return;

    const earned = Math.floor(this.accumulator);
    this.accumulator -= earned;
    this.visibleCount = Math.min(this.visibleCount + earned, this.tokens.length);
    this.publish(this.visibleCount >= this.tokens.length ? 'finished' : 'revealing');
  }

  private publish(phase: PlaybackPhase): void {
    const { messageId } = this.store.getSnapshot();
    this.store.set({
      messageId,
      visibleText: this.tokens.slice(0, this.visibleCount).join(''),
      phase,
    });
  }
}
